/**
 * DSV-016: branch-scoped nullability wording + the required-plus-nullable guard.
 *
 * Kept in its own module (rather than inside `validateRules`) so the check is a
 * pure, independently-testable function and so it warns instead of blocking —
 * existing stored rules use this shape and must keep round-tripping.
 */

/**
 * The one agreed label for every "this value may be null" affordance — the
 * per-branch control in FieldConstraintForm and the property-level control in
 * the base-field editor. Exported so both read from a single source.
 */
export const NULLABLE_LABEL = 'Accept null';

/**
 * Why the required + nullable pair is wrong, in the author's terms.
 *
 * JSON Schema `required` asserts key PRESENCE only — it never asserts
 * non-nullness. So `required: ["action_type"]` alongside
 * `action_type: { type: ["integer", "null"] }` in the SAME branch accepts an
 * explicit `null`, which is exactly what the branch was meant to reject. This
 * is the measured lift_datahub 1.0.2 regression.
 */
export const REQUIRED_NULLABLE_WARNING =
	'“Accept null” contradicts “required” in this branch: required only checks '
	+ 'that the key is present, never that it has a value — so an explicit null '
	+ 'would pass here. If the field may be null when it does not apply, add an '
	+ 'ELSE branch and accept null there instead.';

/**
 * Warn when a single branch field is both listed in the branch's `required` and
 * marked nullable in that branch. Returns the warning text, or `null` when the
 * combination is absent or inert.
 *
 * "Not allowed" and "at least one" rows never contribute a `properties` type
 * pin (emitBranch skips them), so a leftover `nullableInBranch` flag on those
 * rows emits nothing and must not warn.
 */
export function nullableRequiredWarning(field) {
	if (!field || typeof field !== 'object') return null;
	if (field.prohibited || field.atLeastOne) return null;
	if (!field.nullableInBranch) return null;
	if (!field.required) return null;
	return REQUIRED_NULLABLE_WARNING;
}
